import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LOG_ACTION_TYPE_MAP } from 'src/utils';
import { QUERY_KEY_MAP } from 'src/constants';
import { useNotification } from 'src/hooks/use-notification';
import { createOrUpdateStoresPromise } from 'src/utils/api/stores';
import { StoreConfigSchema } from 'src/utils/api/stores/storesSchema';
import { useLogAction } from '../use-log-action';
import { shouldShowStoreError } from './shouldShowStoreError';

export const useCreateStore = () => {
  const queryClient = useQueryClient();

  const { enqueueErrorNotification, enqueueSuccessNotification } = useNotification();

  const { logUserAction } = useLogAction();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: createOrUpdateStoresPromise,
  });

  const createStoreAsync = async (params: StoreConfigSchema) => {
    try {
      const res = await mutateAsync(params);

      if (shouldShowStoreError(res)) {
        enqueueErrorNotification(res ?? 'Error Creating Store');
        return false;
      }

      await Promise.all([
        logUserAction({
          action: 'create_store',
          action_from: '',
          action_to: '',
          type: LOG_ACTION_TYPE_MAP.STORE_MANAGEMENT,
          type_metadata: JSON.stringify({
            uuid: params.uuid || '',
          }),
        }),

        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useGetStore, params.uuid],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_MAP.useListStores],
        }),
      ]);

      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_MAP.useListAuditLogs],
      });

      enqueueSuccessNotification('Store created successfully');
      return true;
    } catch (error) {
      enqueueErrorNotification((error as Error)?.message || 'Error while creating store');

      console.error('createStoreAsync', error);
      return false;
    }
  };

  return {
    createStoreAsync,
    isLoading: isPending,
  };
};
